import Link from 'next/link';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { WarningMessage } from '@/components/Message';
import { Progress } from '@/modules/loyaltyNew/components/Progress';
import { useCMSLoyaltyProgram } from '@/modules/loyaltyNew/hooks/useCMSLoyaltyProgram';
import { useLoyaltyProgress } from '@/modules/loyaltyNew/hooks/useLoyaltyProgress';
import { useGetCommonStore } from '@/store';

import { ScLoyaltyProgramPopup } from './styled';

const LOYALTY_PAGE_PATH = '/loyalty';
const ARROW_ICON_SIZE = '19px';
const LINK_ICON_SIZE = '18px';

interface ILoyaltyProgramPopupProps {
  onLinkClick?: () => void;
}

export const LoyaltyProgramPopup: FC<ILoyaltyProgramPopupProps> = ({
  onLinkClick,
}) => {
  const { t } = useTranslation();

  const useCommonStore = useGetCommonStore();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const [isFullInfoOpen, setIsFullInfoOpen] = useState(() => !isMobileDevice);

  const { data } = useCMSLoyaltyProgram();
  const { lpToSecure, formattedDeadline, timeRemaining, isLPWarningVisible } =
    useLoyaltyProgress();
  const loyaltySseState = useCommonStore.use.loyaltySseState();
  const level = loyaltySseState?.level ?? null;

  const currentLevelData = useMemo(
    () => (level ? data?.levels[level.number - 1] : undefined),
    [data?.levels, level]
  );

  const warningMessage = useMemo(
    () =>
      isLPWarningVisible
        ? t('loyalty.earnToKeepPerks', {
            amount: lpToSecure,
            deadline: formattedDeadline,
          })
        : null,
    [isLPWarningVisible, lpToSecure, formattedDeadline, t]
  );

  const handleToggleFullInfo = () => {
    if (!isMobileDevice) return;
    setIsFullInfoOpen((prev) => !prev);
  };

  if (!(currentLevelData && level)) return null;

  const imageUrl = currentLevelData.image?.url ?? '';

  return (
    <ScLoyaltyProgramPopup onClick={handleToggleFullInfo}>
      <div className="general-info">
        <ImageComponent
          alt={currentLevelData.title}
          height={50}
          src={imageUrl}
          width={50}
        />
        <div>
          <p className="paragraph-1">
            {t('loyalty.level')}: <span>{level.number}</span>
          </p>
          {timeRemaining && (
            <p className="paragraph-1">
              {t('loyalty.levelReviewIn')}: <span>{timeRemaining}</span>
            </p>
          )}
        </div>
        <button
          className={isFullInfoOpen ? 'btn-arrow active' : 'btn-arrow'}
          type="button"
        >
          <IconComponent name="whiteArrowRight" size={ARROW_ICON_SIZE} />
        </button>
      </div>

      <Progress />

      {warningMessage && <WarningMessage message={warningMessage} />}

      {isFullInfoOpen && (
        <Link
          className="link-view-perks"
          href={LOYALTY_PAGE_PATH}
          onClick={onLinkClick}
        >
          <span>{t('loyalty.viewPerks')}</span>
          <IconComponent
            className="blue-arrow-icon"
            name="whiteArrowRight"
            size={LINK_ICON_SIZE}
          />
        </Link>
      )}
    </ScLoyaltyProgramPopup>
  );
};
